const dateFormatters = new Map();

export const getDefaultReportingTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

export function getDateInTimeZone(value, timeZone = getDefaultReportingTimeZone()) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) throw new Error('Choose a valid date.');
  if (!dateFormatters.has(timeZone)) {
    dateFormatters.set(timeZone, new Intl.DateTimeFormat('en-US', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }));
  }
  const parts = Object.fromEntries(dateFormatters.get(timeZone).formatToParts(date)
    .filter((part) => ['year', 'month', 'day'].includes(part.type)).map((part) => [part.type, part.value]));
  return `${parts.year.padStart(4, '0')}-${parts.month}-${parts.day}`;
}

function calendarDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) throw new Error('Choose both a start and end date.');
  const date = new Date(`${value}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== value) {
    throw new Error('Choose valid calendar dates.');
  }
  return date;
}

function addCalendarDays(value, days) {
  const date = calendarDate(value);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

// Find the first instant of the local calendar date rather than adding 24 hours.
// This includes 23/25-hour DST days and zones whose clocks change at midnight.
function startOfCalendarDate(value, timeZone) {
  const reference = calendarDate(value).getTime();
  let lower = reference - 48 * 60 * 60 * 1000;
  let upper = reference + 48 * 60 * 60 * 1000;
  while (lower < upper) {
    const midpoint = lower + Math.floor((upper - lower) / 2);
    if (getDateInTimeZone(midpoint, timeZone) < value) lower = midpoint + 1;
    else upper = midpoint;
  }
  return new Date(lower).toISOString();
}

export function getReportingRange({
  period = 'weekly', startDate, endDate,
  timeZone = getDefaultReportingTimeZone(), now = new Date(),
} = {}) {
  const today = getDateInTimeZone(now, timeZone);
  let first;
  let last;
  if (period === 'weekly') {
    const dayOfWeek = calendarDate(today).getUTCDay();
    first = addCalendarDays(today, -(dayOfWeek === 0 ? 6 : dayOfWeek - 1));
    last = addCalendarDays(first, 6);
  } else if (period === 'monthly') {
    first = `${today.slice(0, 7)}-01`;
    const nextMonth = calendarDate(first);
    nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1);
    last = addCalendarDays(nextMonth.toISOString().slice(0, 10), -1);
  } else if (period === 'custom') {
    calendarDate(startDate);
    calendarDate(endDate);
    first = startDate;
    last = endDate;
    if (first > last) throw new Error('The end date must be on or after the start date.');
  } else {
    throw new Error('Choose a weekly, monthly, or custom reporting period.');
  }
  const startAt = startOfCalendarDate(first, timeZone);
  const endAtExclusive = startOfCalendarDate(addCalendarDays(last, 1), timeZone);
  if (startAt >= endAtExclusive) throw new Error('This range has no calendar days in the selected timezone.');
  return { period, timeZone, startDate: first, endDate: last, startAt, endAtExclusive };
}

export function isInReportingRange(value, range) {
  if (value === undefined || value === null || value === '') return false;
  const instant = value?.toDate ? value.toDate().getTime() : new Date(value).getTime();
  return Number.isFinite(instant) && instant >= Date.parse(range.startAt) && instant < Date.parse(range.endAtExclusive);
}
